import { useEffect, useState } from "react";
import { CheckCircle2, ChevronDown, Sparkles } from "lucide-react";
import { Button } from "./ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "./ui/dialog";
import {
  FALLBACK_RELEASE_NOTES,
  getNextExpandedHistoryIndex,
  isHistoryEntryExpanded,
} from "../lib/releaseNotes";

function VersionMeta({ entry }) {
  if (!entry?.version && !entry?.releasedAt) return null;
  return (
    <div className="flex flex-wrap gap-2 text-xs text-[#6B7280]">
      {entry.version ? <span className="rounded-full bg-[#F5F5F7] px-3 py-1">Version {entry.version}</span> : null}
      {entry.releasedAt ? <span className="rounded-full bg-[#F5F5F7] px-3 py-1">{entry.releasedAt}</span> : null}
    </div>
  );
}

function ReleaseSections({ sections }) {
  return (
    <div className="space-y-5">
      {(sections || []).map((section) => (
        <section key={section.title} className="space-y-3">
          <h3 className="text-sm font-semibold text-[#1D1D1F]">{section.title}</h3>
          <ul className="space-y-2">
            {(section.items || []).map((item) => (
              <li key={item} className="flex gap-3 rounded-xl bg-[#F5F5F7] px-3 py-2.5 text-sm leading-relaxed text-[#3A3A3C]">
                <CheckCircle2 className="mt-0.5 h-4 w-4 shrink-0 text-[#34C759]" />
                <span>{item}</span>
              </li>
            ))}
          </ul>
        </section>
      ))}
    </div>
  );
}

export default function ReleaseNotesDialog({
  open,
  onOpenChange,
  notes,
  loading = false,
  mode = "current",
  onReload,
  reloadLoading = false,
}) {
  const [expandedHistoryIndex, setExpandedHistoryIndex] = useState(null);
  const safeNotes = notes || FALLBACK_RELEASE_NOTES;
  const history = Array.isArray(safeNotes.history) && safeNotes.history.length > 0
    ? safeNotes.history
    : [safeNotes];
  const showHistory = mode === "history";

  useEffect(() => {
    if (open) setExpandedHistoryIndex(null);
  }, [open, showHistory]);

  const title = showHistory ? "Versionsverlauf" : safeNotes.title;
  const description = loading
    ? "Änderungen werden geladen..."
    : showHistory
      ? "Hier siehst du die wichtigsten Änderungen der letzten Versionen."
      : safeNotes.summary;
  const footerClassName = onReload
    ? "border-t border-[#F0F0F2] px-6 py-4 sm:justify-between sm:space-x-0"
    : "border-t border-[#F0F0F2] px-6 py-4 sm:justify-end sm:space-x-0";

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-2xl rounded-2xl p-0 overflow-hidden">
        <div className="border-b border-[#F0F0F2] bg-[#F9F9FB] px-6 py-5">
          <DialogHeader className="space-y-2 pr-6">
            <div className="flex h-10 w-10 items-center justify-center rounded-full bg-[#1D1D1F] text-white">
              <Sparkles className="h-5 w-5" />
            </div>
            <DialogTitle className="text-xl font-semibold text-[#1D1D1F]">{title}</DialogTitle>
            <DialogDescription className="text-sm leading-relaxed text-[#6B7280]">
              {description}
            </DialogDescription>
          </DialogHeader>
        </div>

        <div className="max-h-[58vh] overflow-y-auto px-6 py-5">
          {showHistory ? (
            <div className="space-y-6">
              {history.map((entry, index) => {
                const isLatest = index === 0;
                const isExpanded = isHistoryEntryExpanded(index, expandedHistoryIndex);
                const contentId = `release-history-details-${index}`;

                return (
                  <article
                    key={`${entry.version || "version"}-${index}`}
                    className={index > 0 ? "border-t border-[#F0F0F2] pt-5" : ""}
                  >
                    {isLatest ? (
                      <div className="space-y-2">
                        <VersionMeta entry={entry} />
                        <h3 className="text-base font-semibold text-[#1D1D1F]">{entry.title || "Änderungen"}</h3>
                      </div>
                    ) : (
                      <button
                        type="button"
                        className="flex w-full items-start justify-between gap-4 rounded-xl px-1 py-1 text-left focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#1D1D1F] focus-visible:ring-offset-2"
                        aria-expanded={isExpanded}
                        aria-controls={contentId}
                        onClick={() => setExpandedHistoryIndex((currentIndex) => (
                          getNextExpandedHistoryIndex(currentIndex, index)
                        ))}
                      >
                        <span className="space-y-2">
                          <VersionMeta entry={entry} />
                          <span className="block text-base font-semibold text-[#1D1D1F]">{entry.title || "Änderungen"}</span>
                        </span>
                        <ChevronDown
                          className={`mt-1 h-5 w-5 shrink-0 text-[#6B7280] transition-transform ${isExpanded ? "rotate-180" : ""}`}
                        />
                      </button>
                    )}

                    {isExpanded ? (
                      <div id={contentId} className="mt-4 space-y-4">
                        {entry.summary ? <p className="text-sm leading-relaxed text-[#6B7280]">{entry.summary}</p> : null}
                        <ReleaseSections sections={entry.sections} />
                      </div>
                    ) : null}
                  </article>
                );
              })}
            </div>
          ) : (
            <div className="space-y-5">
              <VersionMeta entry={safeNotes} />
              <ReleaseSections sections={safeNotes.sections} />
            </div>
          )}
        </div>

        <DialogFooter className={footerClassName}>
          <Button className={onReload ? "btn-secondary" : "btn-primary"} variant={onReload ? "outline" : "default"} onClick={() => onOpenChange(false)}>
            Schließen
          </Button>
          {onReload ? (
            <Button className="btn-primary" onClick={onReload} disabled={reloadLoading}>
              {reloadLoading ? "Prüfe..." : "Update jetzt laden"}
            </Button>
          ) : null}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
